"use client";
import React, { useState, useMemo } from "react";
import Link from "next/link";
import { cva } from "class-variance-authority";
import { ServiceCard } from "@/components/ui/cards/serviceCard";
import type { ServiceCardItem } from "@/types/cards";
import {
  AnimatedCard,
  AnimatedCardContainer,
  SlideUpIn,
} from "@/components/animations";
import { Button } from "@/components/ui/buttons";
import { getTranslations, Locale } from "@/lib/i18n/config";
import { useParams } from "next/navigation";
import { ChipFilterGroup } from "../ui/ChipFilterGroup";
import { shuffleArray } from "@/lib/utils";
import { ThemeVariant } from "@/types";
import { extractGlobalTags } from "@/lib/utils/extractGlobalTags";
import { useIsHydrated } from "@/lib/hooks/useIsHydrated";
import { useTranslations } from "@/lib/i18n/useTranslations";

const serviceCardListVariants = cva("container", {
  variants: {
    variant: {
      light: "py-lg",
      dark: "py-lg bg-purple-dark text-white",
    },
  },
  defaultVariants: {
    variant: "light",
  },
});

export interface ServiceCardListProps {
  variant?: ThemeVariant;
  cards: ServiceCardItem[];
  flowy?: boolean;
  className?: string;
  showFilter?: boolean;
  /** Options for "Load More" functionality */
  loadMore?: {
    /** Initial number of items to show */
    initialCount: number;
    /** Number of items to add on each click */
    step: number;
    /** Label for the button */
    buttonLabel?: string;
  };
}

/**
 * ServiceCardList block component that displays service cards in a 3-column grid.
 *
 * Supports a standard grid and a "flowy" staggered layout.
 * Includes optional SEO-friendly "Load More" functionality.
 */
export const ServiceCardList: React.FC<ServiceCardListProps> = ({
  cards,
  variant = "light",
  flowy = false,
  className = "",
  loadMore,
  showFilter,
}) => {
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [visibleCount, setVisibleCount] = useState(
    loadMore?.initialCount ?? cards.length
  );
  const t = useTranslations();

  const isHydrated = useIsHydrated();

  // Shuffle once on the client after hydration; stays stable across
  // re-renders (filtering, "load more") as long as `cards` doesn't change.
  const orderedCards = useMemo(
    () => (isHydrated ? shuffleArray(cards) : cards),
    [isHydrated, cards]
  );

  // Extract unique tags from all cards, sorted alphabetically, excluding "Hot Topic"
  const allTags = useMemo(() => extractGlobalTags(cards), [cards]);

  // Filter cards based on selected tags
  const filteredCards = useMemo(() => {
    if (selectedTags.length === 0) return orderedCards;
    return orderedCards.filter((card) =>
      card.globalTags?.some((tag) => selectedTags.includes(tag.id))
    );
  }, [orderedCards, selectedTags]);

  const displayedCards = useMemo(
    () => filteredCards.slice(0, visibleCount),
    [filteredCards, visibleCount]
  );

  const hasMore = visibleCount < filteredCards.length;

  const handleLoadMore = () => {
    if (loadMore) {
      setVisibleCount((prev) =>
        Math.min(prev + loadMore.step, filteredCards.length)
      );
    }
  };

  const animationDelayIncrement = 0.1;
  const variantPattern = [
    "full-color",
    "full-image",
    "image-top",
    "full-image",
    "image-top",
  ] as const;

  const renderCard = (card: ServiceCardItem, index: number) => {
    const cardVariant = variantPattern[index % variantPattern.length];

    const id = card.id ?? String(index);

    return (
      <AnimatedCard key={`service-card-slide-${id}`} id={id}>
        <SlideUpIn
          key={`service-card-slide-${id}`}
          delay={index * animationDelayIncrement}
        >
          <ServiceCard
            variant={cardVariant}
            color={card.color as "yellow" | "purple-dark"}
            topline={card.topline}
            headline={card.headline}
            text={card.text}
            image={card.image}
            button={card.button}
          />
        </SlideUpIn>
      </AnimatedCard>
    );
  };

  const renderFilterChips = () => {
    if (!showFilter || allTags.length === 0) return;
    const showResetFilterButton = !flowy;

    return (
      <ChipFilterGroup
        variant={variant ?? "light"}
        label={t.common.selectCategory}
        tags={allTags}
        canSelectNone
        showResetButton={showResetFilterButton}
        searchParamName="service-filter"
        onChange={(tags) => {
          setSelectedTags(tags);
          setVisibleCount(loadMore?.initialCount ?? cards.length);
        }}
      />
    );
  };

  if (flowy) {
    return (
      <div className={serviceCardListVariants({ variant, className })}>
        {renderFilterChips()}

        {[0, 1, 2].map((colIndex) => (
          <div
            key={`col-${colIndex}`}
            className={`col-span-12 md:col-span-6 lg:col-span-4 flex flex-col gap-md ${
              colIndex === 1 ? "lg:pt-xl" : ""
            }`}
          >
            <AnimatedCardContainer>
              {displayedCards
                .filter((_, i) => i % 3 === colIndex)
                .map((card, i) => {
                  const globalIndex = i * 3 + colIndex;
                  return renderCard(card, globalIndex);
                })}
            </AnimatedCardContainer>
          </div>
        ))}

        {loadMore && hasMore && (
          <div className="col-span-12 flex justify-center mt-xl">
            <Button
              variant="secondary"
              color={variant === "light" ? "yellow" : "purple"}
              onClick={handleLoadMore}
              label={loadMore.buttonLabel}
              icon="arrow-right"
            />
          </div>
        )}

        {/* Hidden links for SEO to find all content even if JS isn't fully executed or for better indexing */}
        <div className="sr-only" aria-hidden="true">
          {cards.slice(visibleCount).map((card, idx) => (
            <div key={`seo-hidden-${idx}`}>
              <h3>{card.headline}</h3>
              <p>{card.text}</p>
              {card.button?.link && (
                <Link href={card.button.link.href} tabIndex={-1}>
                  {card.button.link.label}
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={serviceCardListVariants({ variant, className })}>
      {renderFilterChips()}

      <div
        className={
          flowy
            ? "grid grid-cols-12 gap-md"
            : "grid grid-cols-12 gap-lg auto-rows-fr"
        }
      >
        {displayedCards.map((card, index) => (
          <div
            key={`grid-item-${index}`}
            className="col-span-12 md:col-span-6 lg:col-span-4"
          >
            {renderCard(card, index)}
          </div>
        ))}
      </div>

      {loadMore && hasMore && (
        <div className="col-span-12 flex justify-center mt-xl">
          <Button
            variant="secondary"
            color={variant === "light" ? "yellow" : "purple"}
            onClick={handleLoadMore}
            label={loadMore.buttonLabel || "Mehr laden"}
            icon="arrow-right"
          />
        </div>
      )}

      {/* Hidden content for SEO indexing of "unloaded" items */}
      <div className="sr-only" aria-hidden="true">
        {cards.slice(visibleCount).map((card, idx) => (
          <div key={`seo-hidden-${idx}`}>
            <h3>{card.headline}</h3>
            <p>{card.text}</p>
            {card.button?.link && (
              <Link href={card.button.link.href} tabIndex={-1}>
                {card.button.link.label}
              </Link>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
